<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
 -->

<template>
  <div class="orangehrm-background-container">
    <leave-conflict
      v-if="showLeaveConflict"
      :workshift-exceeded="isWorkShiftExceeded"
      :data="leaveConflictData"
    ></leave-conflict>
    <div class="orangehrm-card-container">
      <oxd-text tag="h6" class="orangehrm-main-title">
        {{ $t('leave.apply_leave') }}
      </oxd-text>

      <oxd-divider />

      <oxd-text v-if="!isLoading && leaveTypes.length === 0" type="subtitle-2">
        {{ $t('leave.no_leave_types_with_leave_balance') }}
      </oxd-text>

      <oxd-form
        v-else
        ref="formRef"
        :loading="isLoading"
        @submit-valid="onSave"
      >
        <oxd-form-row>
          <oxd-grid :cols="2" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="leave.type"
                type="select"
                :rules="rules.type"
                :options="leaveTypes"
                :label="$t('leave.leave_type')"
                required
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <leave-balance :leave-data="leave"></leave-balance>
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-form-row>
          <oxd-grid :cols="4" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <date-input
                v-model="leave.fromDate"
                :label="$t('general.from_date')"
                :rules="rules.fromDate"
                :years="yearsArray"
                required
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <date-input
                v-model="leave.toDate"
                :label="$t('general.to_date')"
                :rules="rules.toDate"
                :years="yearsArray"
                required
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <!-- Single Day|Duration -->
        <oxd-form-row v-if="appliedLeaveDuration == 1">
          <oxd-grid :cols="4" class="orangehrm-full-width-grid">
            <leave-duration-input
              v-model:duration="leave.duration.type"
              v-model:fromTime="leave.duration.fromTime"
              v-model:toTime="leave.duration.toTime"
              :label="$t('general.duration')"
              :work-shift="workShift"
            ></leave-duration-input>
          </oxd-grid>
        </oxd-form-row>
        <!-- Single Day|Duration -->

        <!-- Partial Day|Duration -->
        <oxd-form-row v-if="appliedLeaveDuration > 1">
          <oxd-grid :cols="4" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="leave.partialOptions"
                type="select"
                :label="$t('leave.partial_days')"
                :options="partialOptions"
              />
            </oxd-grid-item>
            <leave-duration-input
              v-if="showDuration"
              v-model:duration="leave.duration.type"
              v-model:fromTime="leave.duration.fromTime"
              v-model:toTime="leave.duration.toTime"
              :partial="true"
              :label="$t('general.duration')"
              :work-shift="workShift"
            ></leave-duration-input>
            <leave-duration-input
              v-if="showStartDay"
              v-model:duration="leave.duration.type"
              v-model:fromTime="leave.duration.fromTime"
              v-model:toTime="leave.duration.toTime"
              :partial="true"
              :label="$t('leave.start_day')"
              :work-shift="workShift"
            ></leave-duration-input>
            <leave-duration-input
              v-if="showEndDay"
              v-model:duration="leave.endDuration.type"
              v-model:fromTime="leave.endDuration.fromTime"
              v-model:toTime="leave.endDuration.toTime"
              :partial="true"
              :label="$t('leave.end_day')"
              :work-shift="workShift"
            ></leave-duration-input>
          </oxd-grid>
        </oxd-form-row>
        <!-- Partial Day|Duration -->

        <oxd-form-row>
          <oxd-grid :cols="2" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="leave.comment"
                type="textarea"
                :label="$t('general.comments')"
                :rules="rules.comment"
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-form-row>
          <oxd-grid :cols="2" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                type="file"
                :label="$t('leave.attach_document')"
                :button-label="$t('general.browse')"
                :rules="rules.attachment"
                :hint="$t('leave.attachment_hint')"
                @change="onFileChange"
              />
              <ul
                v-if="rawAttachmentFiles.length > 0"
                class="orangehrm-attachment-list"
                style="margin-top: 10px; list-style: none; padding: 0"
              >
                <li
                  v-for="(file, index) in rawAttachmentFiles"
                  :key="index"
                  class="orangehrm-attachment-item"
                  style="display: flex; align-items: center; margin-bottom: 5px"
                >
                  <span class="filename" style="margin-right: 10px"
                    >{{ file.name }} ({{
                      (file.size / 1024).toFixed(2)
                    }}
                    KB)</span
                  >
                  <span
                    style="cursor: pointer; color: #ef5350; font-weight: bold"
                    @click="removeAttachment(index)"
                  >
                    <i class="oxd-icon bi-trash"></i>
                  </span>
                </li>
              </ul>
              <oxd-text tag="p" class="orangehrm-input-hint">
                Attach supporting documents (e.g., Medical Certificate, Travel
                Documents). Max 5MB. Accepted: PDF, JPG, PNG, DOC, DOCX
              </oxd-text>
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-divider />

        <oxd-form-actions>
          <required-text />
          <submit-button :label="$t('general.apply')" />
        </oxd-form-actions>
      </oxd-form>
    </div>
  </div>
</template>

<script>
import {
  required,
  validDateFormat,
  shouldNotExceedCharLength,
  endDateShouldBeAfterStartDate,
} from '@/core/util/validation/rules';
import {yearRange} from '@ohrm/core/util/helper/year-range';
import {diffInDays} from '@ohrm/core/util/helper/datefns';
import {APIService} from '@ohrm/core/util/services/api.service';
import LeaveDurationInput from '@/orangehrmLeavePlugin/components/LeaveDurationInput';
import LeaveBalance from '@/orangehrmLeavePlugin/components/LeaveBalance';
import LeaveConflict from '@/orangehrmLeavePlugin/components/LeaveConflict';
import useLeaveValidators from '@/orangehrmLeavePlugin/util/composable/useLeaveValidators';
import useForm from '@ohrm/core/util/composable/useForm';
import useDateFormat from '@/core/util/composable/useDateFormat';

const leaveModel = {
  type: null,
  fromDate: null,
  toDate: null,
  comment: '',
  attachment: null,
  partialOptions: null,
  duration: {
    type: null,
    fromTime: null,
    toTime: null,
  },
  endDuration: {
    type: null,
    fromTime: null,
    toTime: null,
  },
};

export default {
  name: 'LeaveApply',

  components: {
    'leave-duration-input': LeaveDurationInput,
    'leave-balance': LeaveBalance,
    'leave-conflict': LeaveConflict,
  },

  props: {
    workShift: {
      type: Object,
      default: () => ({}),
    },
  },

  setup() {
    const http = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/leave/leave-requests',
    );
    const {serializeBody, validateOverlapLeaves} = useLeaveValidators(http);
    const {formRef, reset} = useForm();
    const {userDateFormat} = useDateFormat();

    return {
      http,
      reset,
      formRef,
      serializeBody,
      userDateFormat,
      validateOverlapLeaves,
    };
  },

  data() {
    return {
      isLoading: false,
      leave: {...leaveModel, attachments: []},
      // Store a native File/Blob separately to avoid reactive Proxy issues
      rawAttachmentFiles: [],
      rules: {
        type: [required],
        fromDate: [required, validDateFormat(this.userDateFormat)],
        toDate: [
          required,
          validDateFormat(this.userDateFormat),
          endDateShouldBeAfterStartDate(
            () => this.leave.fromDate,
            this.$t('general.to_date_should_be_after_from_date'),
            {allowSameDate: true},
          ),
        ],
        comment: [shouldNotExceedCharLength(250)],
        attachment: [
          (v) => {
            if (!v) return true; // Optional field
            const maxSize = 5 * 1024 * 1024; // 5MB
            if (v.size > maxSize) {
              return this.$t('leave.file_size_exceeded');
            }
            const allowedTypes = [
              'application/pdf',
              'image/jpeg',
              'image/jpg',
              'image/png',
              'application/msword',
              'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            ];
            if (!allowedTypes.includes(v.type)) {
              return this.$t('leave.invalid_file_type');
            }
            return true;
          },
        ],
      },
      partialOptions: [
        {id: 1, label: this.$t('leave.all_days'), key: 'all'},
        {id: 2, label: this.$t('leave.start_day_only'), key: 'start'},
        {id: 3, label: this.$t('leave.end_day_only'), key: 'end'},
        {id: 4, label: this.$t('leave.start_and_end_day'), key: 'start_end'},
      ],
      showLeaveConflict: false,
      isWorkShiftExceeded: false,
      leaveConflictData: null,
      yearsArray: [...yearRange()],
      leaveTypes: [],
    };
  },

  computed: {
    appliedLeaveDuration() {
      return diffInDays(this.leave.fromDate, this.leave.toDate);
    },
    showDuration() {
      const id = this.leave.partialOptions?.id;
      return id && id === 1;
    },
    showStartDay() {
      const id = this.leave.partialOptions?.id;
      return id && (id === 2 || id === 4);
    },
    showEndDay() {
      const id = this.leave.partialOptions?.id;
      return id && (id === 3 || id === 4);
    },
  },

  watch: {
    appliedLeaveDuration: function (duration) {
      if (duration === 1) {
        this.leave.duration.type = {id: 1, label: 'Full Day', key: 'full_day'};
      } else {
        this.leave.duration.type = null;
      }
    },
    'leave.fromDate': function (fromDate) {
      if (!fromDate || this.leave.toDate) return;
      this.leave.toDate = fromDate;
    },
  },

  beforeMount() {
    this.isLoading = true;
    this.http
      .request({
        method: 'GET',
        url: '/api/v2/leave/leave-types/eligible',
      })
      .then((response) => {
        const {data} = response.data;
        this.leaveTypes = data.map((item) => {
          return {
            id: item.id,
            label: item.name,
          };
        });
      })
      .finally(() => {
        this.isLoading = false;
      });
  },

  methods: {
    onFileChange(fileOrEvent) {
      // Support both event payloads and direct file objects
      let newFiles = [];
      if (fileOrEvent && fileOrEvent.target && fileOrEvent.target.files) {
        newFiles = Array.from(fileOrEvent.target.files);
      } else if (Array.isArray(fileOrEvent)) {
        newFiles = fileOrEvent;
      } else if (fileOrEvent) {
        newFiles = [fileOrEvent];
      }

      const maxSize = 5 * 1024 * 1024; // 5MB
      const allowedTypes = [
        'application/pdf',
        'image/jpeg',
        'image/jpg',
        'image/png',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      ];

      newFiles.forEach((file) => {
        // Validate each file
        if (file.size > maxSize) {
          this.$toast.error({
            title: this.$t('general.error'),
            message: `${file.name}: ${this.$t('leave.file_size_exceeded')}`,
          });
          return;
        }
        if (!allowedTypes.includes(file.type)) {
          this.$toast.error({
            title: this.$t('general.error'),
            message: `${file.name}: ${this.$t('leave.invalid_file_type')}`,
          });
          return;
        }

        // Check for duplicates
        if (
          this.rawAttachmentFiles.some(
            (f) => f.name === file.name && f.size === file.size,
          )
        ) {
          return;
        }

        // Process file (handle Proxy/Base64 if necessary, similar to original code)
        let processedFile = file;
        if (
          file &&
          !(file instanceof File) &&
          file.name &&
          file.type &&
          file.size
        ) {
          try {
            let blobData = [];
            if (file.base64) {
              const byteCharacters = atob(file.base64);
              const byteNumbers = new Array(byteCharacters.length);
              for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
              }
              const byteArray = new Uint8Array(byteNumbers);
              blobData = [byteArray.buffer];
            }
            const blob = new Blob(
              blobData.length ? blobData : [new Uint8Array()],
              {type: file.type},
            );
            processedFile = new File([blob], file.name, {type: file.type});
          } catch (err) {
            // console.error('[DEBUG] Failed to construct File:', err);
          }
        }

        this.rawAttachmentFiles.push(processedFile);
        this.leave.attachments.push(fileOrEvent); // Keep original ref just in case
      });
      // Clear the input value so the same file can be selected again if needed (and to reset the UI state)
      // This is a bit of a hack since oxd-input-field doesn't expose a clear method directly easily,
      // but in Vue, the file input's change event doesn't automatically bind back to clear the native input.
      // However, we are pushing to a list, so we don't strictly need to clear it for the model,
      // but the UI might show the last selected filename.
      // Let's rely on the list display for feedback.
    },
    removeAttachment(index) {
      this.rawAttachmentFiles.splice(index, 1);
      this.leave.attachments.splice(index, 1);
    },
    onSave() {
      this.isLoading = true;
      this.showLeaveConflict = false;
      this.leaveConflictData = null;

      this.validateOverlapLeaves(this.leave)
        .then(({isConflict, isOverWorkshift, data}) => {
          if (isConflict) {
            this.leaveConflictData = data;
            this.showLeaveConflict = true;
            this.isWorkShiftExceeded = isOverWorkshift;
            return Promise.reject();
          }
          // Create leave request first
          return this.http.create(this.serializeBody(this.leave));
        })
        .then((response) => {
          // Upload all attachments
          if (
            this.rawAttachmentFiles.length > 0 &&
            response.data &&
            response.data.data &&
            response.data.data.id
          ) {
            const leaveRequestId = response.data.data.id;

            const uploadPromises = this.rawAttachmentFiles.map((file) =>
              this.uploadAttachment(leaveRequestId, file),
            );

            return Promise.all(uploadPromises)
              .then(() => response)
              .catch(() => {
                // Don't fail the whole submission, just show warning
                this.$toast.warn({
                  title: this.$t('general.warning'),
                  message:
                    'Leave submitted but some attachments failed to upload',
                });
                return response;
              });
          }
          return response;
        })
        .then(() => {
          this.$toast.saveSuccess();
          this.reset();
          this.rawAttachmentFiles = [];
          this.leave.attachments = [];
        })
        .catch((error) => {
          if (this.showLeaveConflict) {
            this.$toast.warn({
              title: this.$t('general.warning'),
              message: this.$t('leave.failed_to_submit'),
            });
          } else {
            this.$toast.error({
              title: this.$t('general.error'),
              message:
                error?.response?.data?.message ||
                this.$t('leave.failed_to_submit'),
            });
          }
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    uploadAttachment(leaveRequestId, file) {
      const uploadHttp = new APIService(
        window.appGlobal.baseUrl,
        '/api/v2/leave/leave-requests/attachments',
      );

      const f = file;

      // Convert file to Base64 like OrangeHRM EmployeeAttachmentAPI
      return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = () => {
          if (!reader.result) {
            reject(new Error('FileReader result is empty'));
            return;
          }

          const base64Content = reader.result.split(',')[1]; // Remove data:mime;base64, prefix

          const payload = {
            leaveRequestId: leaveRequestId,
            attachment: {
              name: f.name,
              type: f.type,
              size: f.size,
              base64: base64Content,
            },
          };

          uploadHttp
            .create(payload)
            .then((response) => {
              resolve(response);
            })
            .catch((error) => {
              reject(error);
            });
        };

        reader.onerror = (error) => {
          reject(error);
        };

        reader.onabort = () => {
          reject(new Error('FileReader aborted'));
        };

        if (f instanceof Blob || f instanceof File) {
          reader.readAsDataURL(f);
        } else {
          // Handle case where we might have stored non-File object (should be handled in onFileChange but good to be safe)
          reject(new Error('No readable file content available'));
        }
      });
    },
  },
};
</script>
